import Papa from 'papaparse';
import { getLocalizedField } from './courseDisplay';
import { SUPPORTED_USER_LANGUAGES } from './supportedLanguages';

export type LocalizedTitle = string | Record<string, string> | null | undefined;

export type CurriculumChapter = {
  id: string;
  order: number;
  title: LocalizedTitle;
  totalItems: number;
  requiresRating?: boolean;
};

export type CurriculumWorkshop = {
  id: string;
  chapterId: string;
  order: number;
  title: LocalizedTitle;
};

export type ExportableStudent = {
  student: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  group?: { id: string; name: string } | null;
  progress: {
    progressPercentage: number;
    totalTimeSpent: number;
  };
  chaptersProgress: Array<{
    chapterId: string;
    timeSpent: number;
    completionPercentage?: number;
    rating?: number | null;
  }>;
  workshopsProgress?: Array<{
    workshopItemId: string;
    status?: 'rated' | 'absent' | null;
    rating?: number | null;
  }>;
};

type CsvLabels = {
  lastName: string;
  firstName: string;
  email: string;
  group: string;
  totalCompletion: string;
  totalDuration: string;
  note: string;
  duration: string;
  completion: string;
  notRated: string;
  absent: string;
  noGroup: string;
  chapterFallback: string;
  workshopFallback: string;
};

const CSV_LABELS: Record<string, CsvLabels> = {
  fr: {
    lastName: 'Nom',
    firstName: 'Prénom',
    email: 'Email',
    group: 'Groupe',
    totalCompletion: 'Taux de complétion totale',
    totalDuration: 'Durée totale',
    note: 'Note',
    duration: 'Durée',
    completion: 'Complétion',
    notRated: 'Non noté',
    absent: 'Absent',
    noGroup: 'Sans groupe',
    chapterFallback: 'Chapitre',
    workshopFallback: 'Atelier',
  },
  en: {
    lastName: 'Last name',
    firstName: 'First name',
    email: 'Email',
    group: 'Group',
    totalCompletion: 'Overall completion rate',
    totalDuration: 'Total duration',
    note: 'Rating',
    duration: 'Duration',
    completion: 'Completion',
    notRated: 'Not rated',
    absent: 'Absent',
    noGroup: 'No group',
    chapterFallback: 'Chapter',
    workshopFallback: 'Workshop',
  },
  es: {
    lastName: 'Apellido',
    firstName: 'Nombre',
    email: 'Email',
    group: 'Grupo',
    totalCompletion: 'Tasa de finalización total',
    totalDuration: 'Duración total',
    note: 'Nota',
    duration: 'Duración',
    completion: 'Finalización',
    notRated: 'Sin nota',
    absent: 'Ausente',
    noGroup: 'Sin grupo',
    chapterFallback: 'Capítulo',
    workshopFallback: 'Taller',
  },
  de: {
    lastName: 'Nachname',
    firstName: 'Vorname',
    email: 'E-Mail',
    group: 'Gruppe',
    totalCompletion: 'Gesamtfortschritt',
    totalDuration: 'Gesamtdauer',
    note: 'Note',
    duration: 'Dauer',
    completion: 'Abschluss',
    notRated: 'Nicht bewertet',
    absent: 'Abwesend',
    noGroup: 'Keine Gruppe',
    chapterFallback: 'Kapitel',
    workshopFallback: 'Workshop',
  },
  pt: {
    lastName: 'Apelido',
    firstName: 'Nome',
    email: 'Email',
    group: 'Grupo',
    totalCompletion: 'Taxa de conclusão total',
    totalDuration: 'Duração total',
    note: 'Nota',
    duration: 'Duração',
    completion: 'Conclusão',
    notRated: 'Não avaliado',
    absent: 'Ausente',
    noGroup: 'Sem grupo',
    chapterFallback: 'Capítulo',
    workshopFallback: 'Oficina',
  },
};

const RATING_MAX = 10;

export const getCsvLabels = (language: string): CsvLabels =>
  CSV_LABELS[language] || CSV_LABELS.fr;

export const formatExportDuration = (seconds: number): string => {
  if (!seconds || seconds <= 0) return '0s';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 1) return `${seconds}s`;
  if (minutes < 60) return `${minutes}min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}min`;
};

const formatRatedScore = (rating: number | null | undefined, labels: CsvLabels): string => {
  if (rating == null || Number.isNaN(Number(rating))) return labels.notRated;
  return `${rating}/${RATING_MAX}`;
};

const formatWorkshopCell = (
  status: 'rated' | 'absent' | null | undefined,
  rating: number | null | undefined,
  labels: CsvLabels
): string => {
  if (status === 'absent') return labels.absent;
  if (status === 'rated') return formatRatedScore(rating, labels);
  return labels.notRated;
};

const uniqueColumnTitle = (base: string, used: Set<string>): string => {
  if (!used.has(base)) {
    used.add(base);
    return base;
  }
  let i = 2;
  let next = `${base} (${i})`;
  while (used.has(next)) {
    i += 1;
    next = `${base} (${i})`;
  }
  used.add(next);
  return next;
};

const titleFor = (
  title: LocalizedTitle,
  language: string,
  fallback: string
): string => getLocalizedField(title, language, fallback) || fallback;

export const buildStudentsCsv = ({
  students,
  chapters,
  workshops,
  language,
}: {
  students: ExportableStudent[];
  chapters: CurriculumChapter[];
  workshops: CurriculumWorkshop[];
  language: string;
}): string => {
  const labels = getCsvLabels(language);
  const usedHeaders = new Set<string>();
  const staticHeaders = [
    labels.lastName,
    labels.firstName,
    labels.email,
    labels.group,
    labels.totalCompletion,
    labels.totalDuration,
  ];
  staticHeaders.forEach((h) => usedHeaders.add(h));

  const chapterColumns = chapters.map((chapter, index) => {
    const title = titleFor(
      chapter.title,
      language,
      `${labels.chapterFallback} ${chapter.order ?? index + 1}`
    );
    const includeNote = chapter.requiresRating !== false;
    return {
      id: chapter.id,
      includeNote,
      note: includeNote ? uniqueColumnTitle(`${title} - ${labels.note}`, usedHeaders) : null,
      duration: uniqueColumnTitle(`${title} - ${labels.duration}`, usedHeaders),
      completion: uniqueColumnTitle(`${title} - ${labels.completion}`, usedHeaders),
    };
  });

  const workshopColumns = workshops.map((workshop, index) => {
    const title = titleFor(
      workshop.title,
      language,
      `${labels.workshopFallback} ${workshop.order ?? index + 1}`
    );
    return {
      id: workshop.id,
      header: uniqueColumnTitle(title, usedHeaders),
    };
  });

  const headers = [
    ...staticHeaders,
    ...chapterColumns.flatMap((c) =>
      c.includeNote && c.note ? [c.note, c.duration, c.completion] : [c.duration, c.completion]
    ),
    ...workshopColumns.map((w) => w.header),
  ];

  const rows = students.map((student) => {
    const chapterById = new Map(student.chaptersProgress.map((ch) => [String(ch.chapterId), ch]));
    const workshopById = new Map(
      (student.workshopsProgress || []).map((w) => [String(w.workshopItemId), w])
    );

    const row: string[] = [
      student.student.lastName || '',
      student.student.firstName || '',
      student.student.email || '',
      student.group?.name || labels.noGroup,
      `${student.progress.progressPercentage ?? 0}%`,
      formatExportDuration(student.progress.totalTimeSpent),
    ];

    chapterColumns.forEach((col) => {
      const chapter = chapterById.get(col.id);
      if (col.includeNote) {
        row.push(formatRatedScore(chapter?.rating, labels));
      }
      row.push(
        formatExportDuration(chapter?.timeSpent || 0),
        `${chapter?.completionPercentage ?? 0}%`
      );
    });

    workshopColumns.forEach((col) => {
      const workshop = workshopById.get(col.id);
      row.push(formatWorkshopCell(workshop?.status, workshop?.rating, labels));
    });

    return row;
  });

  return Papa.unparse({ fields: headers, data: rows });
};

export const downloadCsvFile = (filename: string, csv: string): void => {
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const buildStudentsCsvFilename = (courseName: string): string => {
  const safe = (courseName || 'formation')
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  const date = new Date().toISOString().slice(0, 10);
  return `${safe || 'formation'}-apprenants-${date}.csv`;
};

export const resolveExportLanguage = (
  availableLanguages: string[] | undefined,
  defaultLanguage: string | undefined,
  current?: string
): string => {
  const langs =
    availableLanguages && availableLanguages.length > 0
      ? availableLanguages
      : [defaultLanguage || 'fr'];
  if (current && langs.includes(current)) return current;
  if (langs.includes('fr')) return 'fr';
  return langs[0] || 'fr';
};

export const languageOptionsForExport = (availableLanguages: string[] | undefined) => {
  const langs = availableLanguages && availableLanguages.length > 0 ? availableLanguages : ['fr'];
  return langs.map((code) => {
    const known = SUPPORTED_USER_LANGUAGES.find((l) => l.code === code);
    return { code, name: known?.name || code.toUpperCase() };
  });
};
